

import React, { useState } from 'react';
import { KolSquad, Language, UserRole, KolProfile, SquadApplication, SquadMember } from '../types';
import { TRANSLATIONS } from '../translations';
import { Users, Shield, Zap, TrendingUp, CheckCircle, Crown, Plus, X, Save, Lock, UserPlus, FileText, Calendar, DollarSign, ShieldCheck, Mail, AlertCircle, ArrowRight, Percent, PieChart } from 'lucide-react';

interface SquadMarketplaceProps {
  language: Language;
  squads: KolSquad[];
  userRole?: UserRole;
  myKolProfile?: KolProfile;
  onCreateSquad?: (squad: KolSquad) => void;
  allKols: KolProfile[]; // Need this to resolve member IDs
  onBookSquad?: (squad: KolSquad, requirements: string, date: string) => void;
}

export const SquadMarketplace: React.FC<SquadMarketplaceProps> = ({ 
    language, 
    squads: initialSquads, 
    userRole, 
    myKolProfile,
    onCreateSquad,
    allKols,
    onBookSquad
}) => {
  const t = TRANSLATIONS[language];
  // Local state to manage squad updates (applications, members) for the demo
  const [squads, setSquads] = useState<KolSquad[]>(initialSquads);
  
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSquad, setSelectedSquad] = useState<KolSquad | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  
  // Create Squad Form State
  const [squadName, setSquadName] = useState('');
  const [squadDesc, setSquadDesc] = useState('');
  const [discount, setDiscount] = useState<string>('10');
  const [leaderRate, setLeaderRate] = useState<string>('1000'); // Leader sets their own rate

  // Booking Form State
  const [bookingReqs, setBookingReqs] = useState('');
  const [bookingDate, setBookingDate] = useState('');

  // Join Application State
  const [applicationReason, setApplicationReason] = useState('');
  const [proposedRate, setProposedRate] = useState('');

  // Eligibility Check
  const MIN_TRUST_SCORE = 80;
  const canCreate = myKolProfile && myKolProfile.trustScore >= MIN_TRUST_SCORE;

  const handleCreate = () => {
      if (!myKolProfile || !onCreateSquad || !canCreate) return;

      const myRate = Number(leaderRate) || 1000;
      const discPercent = Number(discount) || 0;
      // Initial total price is just the leader's rate minus discount
      const finalPrice = myRate * (1 - discPercent / 100);

      const newSquad: KolSquad = {
          id: `sqd_${Date.now()}`,
          name: squadName,
          description: squadDesc,
          coverImage: 'https://picsum.photos/400/200?random=' + Math.floor(Math.random() * 100),
          leaderId: myKolProfile.id,
          members: [{ kolId: myKolProfile.id, rate: myRate }], // Leader starts as member with rate
          totalFollowers: myKolProfile.totalFollowers,
          totalPrice: finalPrice, 
          discountPercentage: discPercent,
          tags: ['New', 'Custom'],
          applications: []
      };

      setSquads([...squads, newSquad]); // Update local state
      onCreateSquad(newSquad); // Propagate up if needed
      setIsCreateModalOpen(false);
      setSquadName('');
      setSquadDesc('');
      setDiscount('10');
      setLeaderRate('1000');
      alert(t.squadCreated);
  };

  const handleOpenJoinModal = () => {
      setIsJoinModalOpen(true);
  };

  const handleSubmitApplication = () => {
      if (!selectedSquad || !myKolProfile || !applicationReason.trim() || !proposedRate) return;

      const newApplication: SquadApplication = {
          id: `app_${Date.now()}`,
          kolId: myKolProfile.id,
          kolName: myKolProfile.name,
          kolAvatar: myKolProfile.avatar,
          kolHandle: myKolProfile.handle,
          reason: applicationReason,
          proposedRate: Number(proposedRate),
          date: new Date().toISOString().split('T')[0],
          status: 'PENDING'
      };

      const updatedSquads = squads.map(s => {
          if (s.id === selectedSquad.id) {
              return {
                  ...s,
                  applications: [...(s.applications || []), newApplication]
              };
          }
          return s;
      });

      setSquads(updatedSquads);
      setIsJoinModalOpen(false);
      setApplicationReason('');
      setProposedRate('');
      
      alert(`${t.joinRequestSent}: ${t.joinRequestDesc}`);
      setSelectedSquad(null); 
  };

  const handleApproveMember = (squadId: string, appId: string, kolId: string, rate: number) => {
      const updatedSquads = squads.map(s => {
          if (s.id === squadId) {
              const newMember: SquadMember = { kolId, rate };
              const newMembers = [...s.members, newMember];
              const newApps = (s.applications || []).filter(app => app.id !== appId);
              
              // Recalculate stats
              const newMemberProfile = allKols.find(k => k.id === kolId);
              const additionalFollowers = newMemberProfile ? newMemberProfile.totalFollowers : 0;
              
              // Recalculate Total Price
              const totalValue = newMembers.reduce((acc, m) => acc + m.rate, 0);
              const newTotalPrice = totalValue * (1 - s.discountPercentage / 100);

              return {
                  ...s,
                  members: newMembers,
                  applications: newApps,
                  totalFollowers: s.totalFollowers + additionalFollowers,
                  totalPrice: newTotalPrice
              };
          }
          return s;
      });
      setSquads(updatedSquads);
      // If currently managing this squad, ensure the view updates
      const updatedSquad = updatedSquads.find(s => s.id === squadId);
      if (updatedSquad && selectedSquad?.id === squadId) setSelectedSquad(updatedSquad);
  };

  const handleRejectMember = (squadId: string, appId: string) => {
      const updatedSquads = squads.map(s => {
          if (s.id === squadId) {
              const newApps = (s.applications || []).filter(app => app.id !== appId);
              return { ...s, applications: newApps };
          }
          return s;
      });
      setSquads(updatedSquads);
      const updatedSquad = updatedSquads.find(s => s.id === squadId);
      if (updatedSquad && selectedSquad?.id === squadId) setSelectedSquad(updatedSquad);
  };

  const openBookingModal = () => {
      // Pre-fill date with 7 days from now
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      setBookingDate(nextWeek.toISOString().split('T')[0]);
      setIsBookingModalOpen(true);
  };

  const confirmBooking = () => {
      if (selectedSquad && onBookSquad && bookingReqs && bookingDate) {
          onBookSquad(selectedSquad, bookingReqs, bookingDate);
          setIsBookingModalOpen(false);
          setSelectedSquad(null);
          setBookingReqs('');
      }
  };

  const isMember = (squad: KolSquad) => {
      return myKolProfile && squad.members.some(m => m.kolId === myKolProfile.id);
  };

  const isLeader = (squad: KolSquad) => {
      return myKolProfile && squad.leaderId === myKolProfile.id;
  };

  const hasPendingApplications = (squad: KolSquad) => {
      return squad.applications && squad.applications.length > 0;
  };

  const getMemberProfiles = (members: SquadMember[]) => {
      return members.map(m => {
          const profile = allKols.find(k => k.id === m.kolId);
          return profile ? { profile, rate: m.rate } : null;
      }).filter(Boolean) as { profile: KolProfile, rate: number }[];
  };

  // Helper to calculate total value before discount
  const getTotalSquadValue = (squad: KolSquad) => {
      return squad.members.reduce((acc, m) => acc + m.rate, 0);
  };

  // --- Revenue Distribution Calculation ---
  const calculateRevenueShare = (memberRate: number, totalRate: number, totalPrice: number) => {
      // Logic:
      // Gross = totalPrice
      // Platform Fee = 20%
      // Net = Gross * 0.8
      // Leader Fee = 5% of Net
      // Distributable = Net * 0.95
      // Share = (memberRate / totalRate) * Distributable
      
      const gross = totalPrice;
      const net = gross * 0.8;
      const leaderFee = net * 0.05;
      const distributable = net * 0.95;
      
      const share = (memberRate / totalRate) * distributable;
      return share;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-indigo-400" />
            {t.squadMarketplace}
            </h1>
            <p className="text-slate-400 mt-1">{t.squadSubtitle}</p>
        </div>
        {userRole === UserRole.KOL && (
            <div className="relative group">
                <button 
                    onClick={() => canCreate && setIsCreateModalOpen(true)}
                    disabled={!canCreate}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors shadow-lg ${
                        canCreate 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-500/20' 
                        : 'bg-slate-700 text-slate-500 cursor-not-allowed border border-slate-600'
                    }`}
                >
                    {canCreate ? <Plus size={18} /> : <Lock size={16} />}
                    {t.createSquad}
                </button>

                {/* Requirement Tooltip */}
                {!canCreate && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-xl z-20 hidden group-hover:block animate-in fade-in slide-in-from-top-2">
                        <h4 className="text-xs font-bold text-white mb-2 uppercase flex items-center gap-2">
                            <Shield size={12} className="text-indigo-400" />
                            {t.squadRequirements}
                        </h4>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-400">{t.trustScore} &ge; {MIN_TRUST_SCORE}</span>
                                {myKolProfile && (
                                    <span className={myKolProfile.trustScore >= MIN_TRUST_SCORE ? "text-emerald-400" : "text-rose-400 font-bold"}>
                                        {myKolProfile.trustScore}
                                    </span>
                                )}
                            </div>
                            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                    <div 
                                    className={`h-full ${myKolProfile && myKolProfile.trustScore >= MIN_TRUST_SCORE ? 'bg-emerald-500' : 'bg-rose-500'}`} 
                                    style={{ width: `${Math.min(100, ((myKolProfile?.trustScore || 0) / MIN_TRUST_SCORE) * 100)}%` }}
                                    ></div>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-1">
                                {t.increaseTrustDesc}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {squads.map(squad => (
              <div 
                key={squad.id} 
                onClick={() => setSelectedSquad(squad)}
                className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden group hover:border-indigo-500/50 transition-all cursor-pointer relative"
              >
                  {/* Leader Notification Badge */}
                  {isLeader(squad) && hasPendingApplications(squad) && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 rounded-full animate-pulse z-20 border-2 border-slate-900"></div>
                  )}

                  <div className="h-48 relative">
                      <img src={squad.coverImage} alt={squad.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                          <h3 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
                              {squad.name}
                              {myKolProfile?.id === squad.leaderId && (
                                  <Crown size={16} className="text-yellow-400" fill="currentColor" />
                              )}
                          </h3>
                          <div className="flex gap-2">
                              {squad.tags.map(tag => (
                                  <span key={tag} className="text-[10px] bg-indigo-600 px-2 py-0.5 rounded text-white font-bold">{tag}</span>
                              ))}
                          </div>
                      </div>
                      <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg">
                          -{squad.discountPercentage}% OFF
                      </div>
                      
                      {/* Leader Verified Badge */}
                      <div className="absolute top-4 left-4 bg-black/50 backdrop-blur-sm border border-white/10 text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1">
                          <Crown size={10} className="text-yellow-400" /> {t.leaderVerified}
                      </div>
                  </div>

                  <div className="p-6">
                      <p className="text-slate-300 text-sm mb-6 line-clamp-2">{squad.description}</p>

                      <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                              <Users size={20} className="mx-auto mb-1 text-blue-400" />
                              <p className="text-[10px] text-slate-500 uppercase">{t.squadMembers}</p>
                              <p className="font-bold text-white">{squad.members.length}</p>
                          </div>
                          <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                              <TrendingUp size={20} className="mx-auto mb-1 text-emerald-400" />
                              <p className="text-[10px] text-slate-500 uppercase">{t.combinedReach}</p>
                              <p className="font-bold text-white">{(squad.totalFollowers / 1000).toFixed(0)}k+</p>
                          </div>
                          <div className="text-center p-3 bg-slate-900/50 rounded-lg">
                              <Shield size={20} className="mx-auto mb-1 text-purple-400" />
                              <p className="text-[10px] text-slate-500 uppercase">Trust Score</p>
                              <p className="font-bold text-white">95+</p>
                          </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                          <div>
                              <p className="text-xs text-slate-500">{t.bundlePrice}</p>
                              <div className="flex items-end gap-2">
                                  <span className="text-2xl font-bold text-white">${squad.totalPrice.toLocaleString()}</span>
                                  <span className="text-sm text-slate-500 line-through mb-1">
                                      ${getTotalSquadValue(squad).toLocaleString()}
                                  </span>
                              </div>
                          </div>
                          <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
                              {t.squadDetails}
                          </button>
                      </div>
                  </div>
              </div>
          ))}
      </div>

      {/* Create Squad Modal */}
      {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex justify-between items-center p-6 border-b border-slate-700">
                      <h2 className="text-xl font-bold text-white flex items-center gap-2">
                          <UserPlus className="text-indigo-400" />
                          {t.createSquad}
                      </h2>
                      <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                          <X size={20} />
                      </button>
                  </div>
                  <div className="p-6 space-y-4">
                      {/* Explainer */}
                      <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-lg p-4 flex gap-3">
                          <ShieldCheck className="text-indigo-400 flex-shrink-0" size={20} />
                          <p className="text-xs text-indigo-200 leading-relaxed">
                              {t.leaderExplanation}
                          </p>
                      </div>

                      <div>
                          <label className="block text-sm font-medium text-slate-300 mb-1">{t.squadName}</label>
                          <input 
                              type="text" 
                              value={squadName}
                              onChange={(e) => setSquadName(e.target.value)}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                      </div>
                      <div>
                          <label className="block text-sm font-medium text-slate-300 mb-1">{t.squadDescription}</label>
                          <textarea 
                              rows={3}
                              value={squadDesc}
                              onChange={(e) => setSquadDesc(e.target.value)}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">{t.enterLeaderRate}</label>
                              <div className="relative">
                                  <span className="absolute left-3 top-2.5 text-slate-500">$</span>
                                  <input 
                                    type="number" 
                                    value={leaderRate}
                                    onChange={(e) => setLeaderRate(e.target.value)}
                                    placeholder="1000"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-6 pr-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                  />
                              </div>
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-slate-300 mb-1">{t.discount} %</label>
                              <input 
                                  type="number" 
                                  value={discount}
                                  onChange={(e) => setDiscount(e.target.value)}
                                  placeholder="10"
                                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                              />
                          </div>
                      </div>

                      {/* Pricing Preview */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                          <div className="flex justify-between items-center text-sm text-slate-400 mb-1">
                               <span>{t.totalSquadValue} (Initial)</span>
                               <span className="line-through">${Number(leaderRate).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm text-emerald-400 mb-2">
                               <span>Discount ({discount}%)</span>
                               <span>-${(Number(leaderRate) * (Number(discount)/100)).toFixed(0)}</span>
                          </div>
                          <div className="border-t border-slate-700/50 pt-2 flex justify-between items-center">
                               <span className="font-bold text-white">{t.bundlePrice}</span>
                               <span className="font-bold text-xl text-white">${(Number(leaderRate) * (1 - Number(discount)/100)).toFixed(0)}</span>
                          </div>
                      </div>

                      <div className="pt-2">
                          <button 
                              onClick={handleCreate}
                              disabled={!squadName || !squadDesc}
                              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
                          >
                              <Save size={18} /> {t.createSquad}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* Squad Detail Modal */}
      {selectedSquad && !isBookingModalOpen && !isJoinModalOpen && !isManageModalOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                  {/* Header Image */}
                  <div className="h-48 relative flex-shrink-0">
                      <img src={selectedSquad.coverImage} alt="" className="w-full h-full object-cover rounded-t-xl opacity-60" />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                      <button onClick={() => setSelectedSquad(null)} className="absolute top-4 right-4 bg-black/50 hover:bg-black/80 text-white p-2 rounded-full transition-colors z-10">
                          <X size={20} />
                      </button>
                      
                      <div className="absolute bottom-6 left-8">
                          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
                              {selectedSquad.name}
                              <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded shadow-lg">-{selectedSquad.discountPercentage}% OFF</span>
                          </h2>
                          <p className="text-slate-300 max-w-xl">{selectedSquad.description}</p>
                      </div>

                      {/* Leader Controls */}
                      {isLeader(selectedSquad) && (
                          <button 
                            onClick={() => setIsManageModalOpen(true)}
                            className="absolute bottom-6 right-8 bg-slate-900/80 hover:bg-slate-800 text-white px-4 py-2 rounded-lg border border-slate-600 flex items-center gap-2 transition-all"
                          >
                              <ShieldCheck size={16} className="text-indigo-400" /> 
                              {t.manageApplications}
                              {hasPendingApplications(selectedSquad) && (
                                  <span className="w-5 h-5 bg-rose-500 rounded-full flex items-center justify-center text-[10px] font-bold">
                                      {selectedSquad.applications?.length}
                                  </span>
                              )}
                          </button>
                      )}
                  </div>

                  <div className="p-8 overflow-y-auto">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Members List */}
                          <div>
                              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                  <Users className="text-indigo-400" /> {t.meetTheSquad}
                              </h3>
                              <div className="space-y-3">
                                  {getMemberProfiles(selectedSquad.members).map(({ profile, rate }) => (
                                      <div key={profile.id} className="bg-slate-700/30 border border-slate-700 rounded-xl p-3 flex items-center gap-3">
                                          <img src={profile.avatar} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-slate-600" />
                                          <div className="flex-1">
                                              <p className="text-white font-bold text-sm flex items-center gap-1">
                                                  {profile.name}
                                                  {profile.id === selectedSquad.leaderId && <Crown size={12} className="text-yellow-400" fill="currentColor" />}
                                              </p>
                                              <p className="text-xs text-slate-400">{profile.handle}</p>
                                          </div>
                                          <div className="text-right">
                                              <p className="text-white font-mono text-sm">${rate}</p>
                                              <p className="text-[10px] text-slate-500">Rate</p>
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          </div>

                          {/* Revenue Distribution (For Transparency) */}
                          <div className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
                                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2 uppercase tracking-wide">
                                    <PieChart size={16} className="text-emerald-400" /> {t.revenueSplit}
                                </h3>
                                
                                <div className="space-y-4">
                                    <div className="flex justify-between text-xs text-slate-400 border-b border-slate-700/50 pb-2">
                                        <span>{t.totalPrice} (Gross)</span>
                                        <span className="text-white font-bold">${selectedSquad.totalPrice.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-slate-400">
                                        <span>{t.platformFeeLabel}</span>
                                        <span className="text-rose-400">-${(selectedSquad.totalPrice * 0.2).toFixed(0)}</span>
                                    </div>
                                    <div className="flex justify-between text-xs text-slate-400">
                                        <span>{t.leaderFee}</span>
                                        <span className="text-amber-400">-${(selectedSquad.totalPrice * 0.8 * 0.05).toFixed(0)}</span>
                                    </div>
                                    
                                    <div className="mt-4 pt-4 border-t border-slate-700">
                                        <p className="text-xs font-bold text-slate-300 mb-2 uppercase">{t.estimatedPayout}</p>
                                        <div className="space-y-2">
                                            {getMemberProfiles(selectedSquad.members).map(({ profile, rate }) => {
                                                const share = calculateRevenueShare(rate, getTotalSquadValue(selectedSquad), selectedSquad.totalPrice);
                                                const isLeader = profile.id === selectedSquad.leaderId;
                                                const bonus = isLeader ? (selectedSquad.totalPrice * 0.8 * 0.05) : 0;
                                                const totalPayout = share + bonus;

                                                return (
                                                    <div key={profile.id} className="flex justify-between items-center text-xs">
                                                        <span className="text-slate-400 flex items-center gap-1">
                                                            {profile.handle}
                                                            {isLeader && <Crown size={10} className="text-yellow-400" />}
                                                        </span>
                                                        <span className="text-emerald-400 font-mono font-bold">
                                                            ${totalPayout.toFixed(0)}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                          </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="pt-6 mt-6 border-t border-slate-700 flex flex-col md:flex-row justify-between items-center gap-6">
                          <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700 w-full md:w-auto min-w-[300px]">
                              <div className="flex justify-between items-center mb-1 text-sm">
                                  <span className="text-slate-400">{t.totalSquadValue}</span>
                                  <span className="text-slate-400 line-through decoration-rose-500/50 decoration-2">${getTotalSquadValue(selectedSquad).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between items-center mb-3 text-sm">
                                  <span className="text-slate-400">Squad Discount</span>
                                  <span className="text-emerald-400 flex items-center gap-1"><Percent size={12} /> {selectedSquad.discountPercentage}%</span>
                              </div>
                              <div className="flex justify-between items-end border-t border-slate-700/50 pt-2">
                                  <span className="text-white font-medium">{t.bundlePrice}</span>
                                  <span className="text-3xl font-bold text-white">${selectedSquad.totalPrice.toLocaleString()}</span>
                              </div>
                          </div>
                          
                          <div className="flex gap-3 w-full md:w-auto">
                              {userRole === UserRole.PROJECT ? (
                                  <button 
                                      onClick={openBookingModal}
                                      className="flex-1 md:flex-none px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
                                  >
                                      <Zap size={20} /> {t.bookSquad}
                                  </button>
                              ) : (
                                  !isMember(selectedSquad) ? (
                                      <button 
                                          onClick={handleOpenJoinModal}
                                          className="flex-1 md:flex-none px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-bold transition-colors"
                                      >
                                          {t.requestToJoin}
                                      </button>
                                  ) : (
                                      <button disabled className="flex-1 md:flex-none px-6 py-3 bg-slate-800 text-slate-500 rounded-xl font-bold border border-slate-700 cursor-not-allowed">
                                          {t.alreadyMember}
                                      </button>
                                  )
                              )}
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* Booking Modal */}
      {isBookingModalOpen && selectedSquad && (
           <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
               <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                   <div className="flex justify-between items-center p-6 border-b border-slate-700">
                       <h2 className="text-xl font-bold text-white flex items-center gap-2">
                           <Zap className="text-indigo-400" />
                           {t.squadBookingTitle}
                       </h2>
                       <button onClick={() => setIsBookingModalOpen(false)} className="text-slate-400 hover:text-white">
                           <X size={20} />
                       </button>
                   </div>
                   
                   <div className="p-6 space-y-6">
                       {/* Summary Card */}
                       <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                           <h4 className="text-sm font-bold text-slate-300 mb-3 uppercase tracking-wider">{t.squadSummary}</h4>
                           <div className="flex items-center gap-3 mb-4">
                               <img src={selectedSquad.coverImage} alt="" className="w-12 h-12 rounded-lg object-cover" />
                               <div>
                                   <p className="font-bold text-white">{selectedSquad.name}</p>
                                   <p className="text-xs text-slate-400">{selectedSquad.members.length} {t.squadMembers}</p>
                               </div>
                           </div>
                           <div className="flex justify-between items-center text-sm border-t border-slate-700/50 pt-2">
                               <span className="text-slate-400">{t.totalPrice}</span>
                               <span className="font-bold text-white">${selectedSquad.totalPrice.toLocaleString()}</span>
                           </div>
                       </div>

                       {/* Form */}
                       <div className="space-y-4">
                           <div>
                               <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center gap-2">
                                   <FileText size={14} /> {t.campaignRequirements}
                               </label>
                               <textarea 
                                   rows={3}
                                   value={bookingReqs}
                                   onChange={(e) => setBookingReqs(e.target.value)}
                                   placeholder="Describe your campaign goals, deliverables..."
                                   className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                               />
                           </div>
                           <div>
                               <label className="block text-sm font-medium text-slate-300 mb-1 flex items-center gap-2">
                                   <Calendar size={14} /> {t.campaignDeadline}
                               </label>
                               <input 
                                   type="date"
                                   value={bookingDate}
                                   onChange={(e) => setBookingDate(e.target.value)}
                                   className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                               />
                           </div>
                       </div>

                       {/* Confirmation */}
                       <div className="pt-2">
                           <button 
                               onClick={confirmBooking}
                               disabled={!bookingReqs || !bookingDate}
                               className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
                           >
                               <DollarSign size={18} /> {t.confirmBooking} - ${selectedSquad.totalPrice.toLocaleString()}
                           </button>
                       </div>
                   </div>
               </div>
           </div>
      )}

      {/* Application Form Modal (For Joiners) */}
      {isJoinModalOpen && selectedSquad && (
           <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
               <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                   <div className="flex justify-between items-center p-6 border-b border-slate-700">
                       <h2 className="text-xl font-bold text-white flex items-center gap-2">
                           <Mail className="text-indigo-400" />
                           {t.requestToJoin}
                       </h2>
                       <button onClick={() => setIsJoinModalOpen(false)} className="text-slate-400 hover:text-white">
                           <X size={20} />
                       </button>
                   </div>
                   
                   <div className="p-6 space-y-6">
                       <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
                            <h4 className="font-bold text-white mb-1">{selectedSquad.name}</h4>
                            <p className="text-sm text-slate-400">{t.whyJoinSquad}</p>
                       </div>

                       <div>
                           <label className="block text-sm font-medium text-slate-300 mb-1">{t.applicationReason}</label>
                           <textarea 
                               rows={3}
                               value={applicationReason}
                               onChange={(e) => setApplicationReason(e.target.value)}
                               placeholder="Explain how your audience fits this squad..."
                               className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                           />
                       </div>

                       <div>
                           <label className="block text-sm font-medium text-slate-300 mb-1">{t.enterProposedRate}</label>
                           <div className="relative">
                               <span className="absolute left-3 top-2.5 text-slate-500">$</span>
                               <input 
                                    type="number" 
                                    value={proposedRate}
                                    onChange={(e) => setProposedRate(e.target.value)}
                                    placeholder="500"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-6 pr-4 py-2.5 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                               />
                           </div>
                           <p className="text-xs text-slate-500 mt-1">This will be your share of the Total Squad Value.</p>
                       </div>

                       <div className="pt-2">
                           <button 
                               onClick={handleSubmitApplication}
                               disabled={!applicationReason.trim() || !proposedRate}
                               className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-bold shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2"
                           >
                               <ArrowRight size={18} /> {t.requestToJoin}
                           </button>
                       </div>
                   </div>
               </div>
           </div>
      )}

      {/* Leader Management Modal (For Squad Owners) */}
      {isManageModalOpen && selectedSquad && (
           <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
               <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
                   <div className="flex justify-between items-center p-6 border-b border-slate-700 bg-slate-900/50 rounded-t-xl">
                       <h2 className="text-xl font-bold text-white flex items-center gap-2">
                           <ShieldCheck className="text-indigo-400" />
                           {t.manageApplications}
                       </h2>
                       <button onClick={() => setIsManageModalOpen(false)} className="text-slate-400 hover:text-white">
                           <X size={20} />
                       </button>
                   </div>
                   
                   <div className="p-6 overflow-y-auto bg-slate-800">
                       <h3 className="text-sm font-bold text-slate-400 uppercase mb-4">{t.pendingApplications}</h3>
                       
                       {(!selectedSquad.applications || selectedSquad.applications.length === 0) ? (
                           <div className="p-8 text-center border border-dashed border-slate-700 rounded-xl text-slate-500">
                               <AlertCircle className="mx-auto mb-2 opacity-50" />
                               {t.noApplications}
                           </div>
                       ) : (
                           <div className="space-y-4">
                               {selectedSquad.applications.map(app => (
                                   <div key={app.id} className="bg-slate-900/50 border border-slate-700 rounded-xl p-5">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <img src={app.kolAvatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                                                <div>
                                                    <p className="font-bold text-white">{app.kolName}</p>
                                                    <p className="text-xs text-slate-400">{app.kolHandle}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-emerald-400 font-mono font-bold block">${app.proposedRate}</span>
                                                <span className="text-xs text-slate-500">{app.date}</span>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-slate-800 p-4 rounded-lg text-sm text-slate-300 italic mb-4 border border-slate-700/50">
                                            "{app.reason}"
                                        </div>

                                        <div className="flex gap-3">
                                            <button 
                                                onClick={() => handleRejectMember(selectedSquad.id, app.id)}
                                                className="flex-1 py-2 bg-slate-800 hover:bg-rose-900/20 text-slate-400 hover:text-rose-400 border border-slate-700 hover:border-rose-500/30 rounded-lg transition-colors text-sm font-bold"
                                            >
                                                {t.reject}
                                            </button>
                                            <button 
                                                onClick={() => handleApproveMember(selectedSquad.id, app.id, app.kolId, app.proposedRate)}
                                                className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm font-bold shadow-lg shadow-emerald-500/20"
                                            >
                                                {t.approve}
                                            </button>
                                        </div>
                                   </div>
                               ))}
                           </div>
                       )}
                   </div>
               </div>
           </div>
      )}
    </div>
  );
};
